import { useCallback, useRef, useState, type ChangeEvent } from 'react';

import type { ExportEnvelope } from '../../domain/backup/ExportEnvelope';
import type { BuildInfo } from '../../application/dto/BuildInfo';
import type { DailyMissionsClient } from '../../application/services/DailyMissionsService';
import type { ImportFilePreview } from '../../application/use-cases/PreviewImportFile';
import { Button } from '../components/Button';
import { Dialog } from '../components/Dialog';
import { PageHeader } from '../components/PageHeader';
import { useTranslation } from '../design-system/data/translations';
import { useAscendLocale, setLocale } from '../design-system/session/useAscendLocale';
import { loadUiPreferences, saveUiPreferences } from '../design-system/session/uiPreferencesStorage';
import { DEFAULT_LOCALES } from '../design-system/data/locales';

interface SettingsPageProps {
  readonly client: DailyMissionsClient;
  readonly buildInfo: BuildInfo;
}

function downloadEnvelope(envelope: ExportEnvelope, filenamePrefix: string): void {
  const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${envelope.exportedAt.slice(0, 10)}.json`;
  link.click();
  // Revoking immediately after click() can race with the browser reading the blob: URL for the
  // download, truncating it — deferring the revoke lets the download actually start first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type ImportState =
  | { readonly stage: 'idle' }
  | { readonly stage: 'preview'; readonly preview: ImportFilePreview }
  | { readonly stage: 'applying'; readonly preview: ImportFilePreview };

export function SettingsPage({ client, buildInfo }: SettingsPageProps) {
  const { t } = useTranslation();
  const currentLocale = useAscendLocale();
  const [firstName, setFirstName] = useState(() => loadUiPreferences().firstName ?? '');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [importState, setImportState] = useState<ImportState>({ stage: 'idle' });
  const [importError, setImportError] = useState<string>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const [justDeleted, setJustDeleted] = useState(false);
  const [justImported, setJustImported] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(undefined);
    const result = await client.exportData();
    setExporting(false);
    if (!result.ok) {
      setExportError(result.error.message);
      return;
    }
    downloadEnvelope(result.value, 'ascend-invictus-export');
  }, [client]);

  const handleFileSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setImportError(undefined);
      setJustImported(false);
      setJustDeleted(false);
      const rawJson = await file.text();
      const result = client.previewImport(rawJson);
      if (!result.ok) {
        setImportError(result.error.message);
        return;
      }
      setImportState({ stage: 'preview', preview: result.value });
    },
    [client],
  );

  const handleConfirmImport = useCallback(async () => {
    if (importState.stage !== 'preview') return;
    setImportState({ stage: 'applying', preview: importState.preview });
    const result = await client.confirmImport(importState.preview.envelope);
    if (!result.ok) {
      setImportError(result.error.message);
      setImportState({ stage: 'idle' });
      return;
    }
    setImportState({ stage: 'idle' });
    setJustImported(true);
    downloadEnvelope(result.value.safetyBackup, 'ascend-invictus-pre-import-backup');
  }, [client, importState]);

  const handleDelete = useCallback(async () => {
    setDeleteBusy(true);
    setDeleteError(undefined);
    setJustImported(false);
    const result = await client.deleteAllData();
    setDeleteBusy(false);
    if (!result.ok) {
      setDeleteError(result.error.message);
      return;
    }
    // Efface également le prénom (donnée personnelle locale).
    const prefs = loadUiPreferences();
    saveUiPreferences({ ...prefs, firstName: '' });
    setFirstName('');
    setDeleteDialogOpen(false);
    setJustDeleted(true);
  }, [client]);

  return (
    <main className="page settings-page">
      <PageHeader
        eyebrow={t('data.ownership_eyebrow')}
        title={t('data.title')}
        description={t('data.description')}
      />

      <section className="settings-section" aria-labelledby="settings-profile-title">
        <h2 id="settings-profile-title">{t('settings.profile')}</h2>
        <p className="settings-section__description">{t('settings.profile_description')}</p>
        <label className="settings-profile-field">
          <span className="settings-profile-field__label">{t('settings.first_name')}</span>
          <input
            type="text"
            className="settings-profile-field__input"
            value={firstName}
            placeholder={t('settings.first_name_placeholder')}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => {
              const prefs = loadUiPreferences();
              saveUiPreferences({ ...prefs, firstName: firstName.trim() });
            }}
            autoComplete="given-name"
            maxLength={60}
          />
        </label>
      </section>

      <section className="settings-section" aria-labelledby="settings-language-title">
        <h2 id="settings-language-title">{t('settings.language')}</h2>
        <p className="settings-section__description">{t('settings.language_description')}</p>
        <div className="settings-language-options" role="radiogroup" aria-label={t('settings.language')}>
          {DEFAULT_LOCALES.map((locale) => (
            <label key={locale.code} className={`settings-language-option${currentLocale === locale.code ? ' settings-language-option--selected' : ''}`}>
              <input
                type="radio"
                name="language"
                value={locale.code}
                checked={currentLocale === locale.code}
                onChange={() => setLocale(locale.code)}
              />
              <span className="settings-language-option__indicator" aria-hidden="true" />
              {locale.label}
            </label>
          ))}
        </div>
      </section>

      {justDeleted ? (
        <p role="status" className="settings-banner">
          {t('data.delete_success')}
        </p>
      ) : null}
      {justImported ? (
        <p role="status" className="settings-banner">
          {t('data.import_success')}
        </p>
      ) : null}

      <section className="settings-section" aria-labelledby="settings-export-title">
        <h2 id="settings-export-title">{t('data.export')}</h2>
        <p className="settings-section__description">{t('data.export_description')}</p>
        <Button onClick={() => void handleExport()} disabled={exporting}>
          {exporting ? t('data.exporting') : t('data.export')}
        </Button>
        {exportError ? (
          <p role="alert" className="settings-error">
            {exportError}
          </p>
        ) : null}
      </section>

      <section className="settings-section" aria-labelledby="settings-import-title">
        <h2 id="settings-import-title">{t('data.import')}</h2>
        <p className="settings-section__description">{t('data.import_description')}</p>
        <Button onClick={() => fileInputRef.current?.click()}>{t('data.choose_file')}</Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="settings-file-input"
          onChange={(event) => void handleFileSelected(event)}
          aria-label={t('data.choose_file_aria')}
        />
        {importError ? (
          <p role="alert" className="settings-error">
            {importError}
          </p>
        ) : null}
      </section>

      <section
        className="settings-section settings-section--danger"
        aria-labelledby="settings-delete-title"
      >
        <h2 id="settings-delete-title">{t('data.delete')}</h2>
        <p className="settings-section__description">{t('data.delete_description')}</p>
        <Button variant="danger" onClick={() => setDeleteDialogOpen(true)}>
          {t('data.delete')}
        </Button>
        {deleteError ? (
          <p role="alert" className="settings-error">
            {deleteError}
          </p>
        ) : null}
      </section>

      <section className="settings-section" aria-labelledby="settings-version-title">
        <h2 id="settings-version-title">{t('version.label')}</h2>
        <dl className="build-info">
          <div>
            <dt>{t('version.field')}</dt>
            <dd>{buildInfo.appVersion}</dd>
          </div>
          <div>
            <dt>{t('version.commit')}</dt>
            <dd>{buildInfo.gitCommit}</dd>
          </div>
          <div>
            <dt>{t('version.build_date')}</dt>
            <dd>{buildInfo.buildDate}</dd>
          </div>
        </dl>
      </section>

      <Dialog
        open={importState.stage !== 'idle'}
        title={t('data.import_confirm')}
        confirmLabel={t('data.import_replace')}
        danger
        busy={importState.stage === 'applying'}
        onConfirm={() => void handleConfirmImport()}
        onClose={() => setImportState({ stage: 'idle' })}
      >
        {importState.stage !== 'idle' ? (
          <div>
            <p>{t('data.import_preview_intro')}</p>
            <ul>
              <li>
                {t('data.import_preview_days').replace(
                  '{count}',
                  String(importState.preview.preview.dayCount),
                )}
              </li>
              <li>
                {t('data.import_preview_missions').replace(
                  '{count}',
                  String(importState.preview.preview.missionCount),
                )}
              </li>
              <li>
                {t('data.import_preview_journal').replace(
                  '{count}',
                  String(importState.preview.preview.journalEntryCount),
                )}
              </li>
              <li>
                {t('data.import_preview_workouts').replace(
                  '{count}',
                  String(importState.preview.preview.workoutSessionCount),
                )}
              </li>
            </ul>
            <p>{t('data.import_warning')}</p>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        title={t('data.delete_title')}
        confirmLabel={t('data.delete_final')}
        danger
        busy={deleteBusy}
        onConfirm={() => void handleDelete()}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <p>{t('data.delete_warning')}</p>
      </Dialog>
    </main>
  );
}
